// ============================================
// Super Jake Provider Router
//
// Model-agnostic failover layer between agents and providers.
// Agents call routedGenerate/routedChat/routedVision instead of
// provider functions directly. The router tries the primary provider
// first, then walks the fallbackChain with circuit-breaker logic.
//
// K=TOP: clinical safety preserved — if ALL providers fail, we throw.
// We NEVER fabricate data as fallback.
// ============================================

import type { SkillConfig, SkillId } from '../../types';
import { SKILL_CONFIGS } from './index';
import { claudeGenerate, claudeChat, claudeVision } from './providers/claude';
import { geminiGenerate, geminiChat, geminiVision } from './providers/gemini';
import type { GeminiMessage, GeminiCallMetadata } from './providers/gemini';
import { redact, rehydrate, redactMessages, mergeRedactionMaps } from './piiRedaction';
import { selectProvider, type FallbackContext } from '../providerRouterService';
import { trackFallbackPerformance } from './fallbackPerformanceTracker';

// --- Types ---

export interface RouterResponse {
  text: string;
  provider: 'claude' | 'gemini' | 'local';
  model: string;
  fallbackUsed: boolean;
  piiRedacted: boolean;
  fallbackWarning?: string;
  attempts: Array<{ provider: string; model: string; error?: string }>;
}

export interface RouterMessage {
  role: 'user' | 'assistant';
  content: string;
}

type ProviderKey = 'claude' | 'gemini' | 'local';

interface ChainEntry {
  provider: ProviderKey;
  model: string;
}

// --- Circuit Breaker ---
// Tracks last failure time per provider. If a provider failed within
// CIRCUIT_BREAKER_MS, skip it and try the next in chain.

const CIRCUIT_BREAKER_MS = 60_000; // 60 seconds

const providerFailures: Record<string, number> = {};

function recordFailure(provider: string): void {
  providerFailures[provider] = Date.now();
}

function recordSuccess(provider: string): void {
  delete providerFailures[provider];
}

function isCircuitOpen(provider: string): boolean {
  const lastFailure = providerFailures[provider];
  if (!lastFailure) return false;
  if (Date.now() - lastFailure > CIRCUIT_BREAKER_MS) {
    // Circuit has recovered — allow retry
    delete providerFailures[provider];
    return false;
  }
  return true;
}

// --- History Translation ---
// Claude uses { role: 'user' | 'assistant', content: string }
// Gemini uses { role: 'user' | 'model', parts: [{ text: string }] }

function toGeminiHistory(messages: RouterMessage[]): GeminiMessage[] {
  return messages.map((m) => ({
    role: m.role === 'assistant' ? 'model' as const : 'user' as const,
    parts: [{ text: m.content }],
  }));
}

function toClaudeHistory(messages: RouterMessage[]): Array<{ role: 'user' | 'assistant'; content: string }> {
  return messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));
}

// --- Build Chain ---
// Returns the ordered list of providers to try: primary first, then fallbacks.

function buildChain(config: SkillConfig, overrides?: Partial<SkillConfig>): ChainEntry[] {
  const mergedConfig = overrides ? { ...config, ...overrides } : config;
  const chain = mergedConfig.fallbackChain;

  if (chain && chain.length > 0) {
    return chain as ChainEntry[];
  }

  // No fallback chain defined — just the primary
  return [{ provider: mergedConfig.provider as ProviderKey, model: mergedConfig.model }];
}

// --- Core Router Logic ---

async function routeWithFallback(
  skillId: SkillId,
  overrides: Partial<SkillConfig> | undefined,
  callProvider: (entry: ChainEntry, config: SkillConfig) => Promise<string>,
  fallbackContext?: FallbackContext,
): Promise<RouterResponse> {
  const config = SKILL_CONFIGS[skillId];
  if (!config) {
    throw new Error(`[Provider Router] Unknown skill: ${skillId}`);
  }

  const chain = buildChain(config, overrides);
  const mergedConfig = overrides ? { ...config, ...overrides } : config;
  const attempts: Array<{ provider: string; model: string; error?: string }> = [];
  let fallbackUsed = false;

  for (let i = 0; i < chain.length; i++) {
    const entry = chain[i];

    // Skip 'local' provider — placeholder for future MedGemma on-premise
    if (entry.provider === 'local') {
      const msg = 'Local provider not yet available';
      console.warn(`[Provider Router] ${skillId}: skipping ${entry.provider}/${entry.model} — ${msg}`);
      attempts.push({ provider: entry.provider, model: entry.model, error: msg });
      fallbackUsed = true;
      continue;
    }

    // Circuit breaker check
    if (isCircuitOpen(entry.provider)) {
      const msg = `Circuit open (failed within last ${CIRCUIT_BREAKER_MS / 1000}s)`;
      console.warn(`[Provider Router] ${skillId}: skipping ${entry.provider}/${entry.model} — ${msg}`);
      attempts.push({ provider: entry.provider, model: entry.model, error: msg });
      fallbackUsed = true;
      continue;
    }

    // --- Fallback Validation Gate ---
    // When falling back (i > 0), validate the candidate model before use.
    // Primary provider (i === 0) is pre-approved and does not need validation.
    if (i > 0) {
      const currentProvider = chain[0].provider;
      const ctx: FallbackContext = fallbackContext ?? { isPatientSessionActive: false };
      const validationResult = selectProvider(
        skillId,
        currentProvider,
        [entry], // Validate this specific candidate
        ctx,
      );

      if (validationResult.status === 'no_available_provider') {
        const msg = `Fallback validation failed: ${validationResult.rejectionReason ?? 'rejected'}`;
        console.error(`[Provider Router] ${skillId}: ${entry.provider}/${entry.model} — ${msg}`);
        attempts.push({ provider: entry.provider, model: entry.model, error: msg });
        fallbackUsed = true;
        continue; // Skip this candidate, try next in chain
      }

      console.log(
        `[Provider Router] ${skillId}: ${entry.provider}/${entry.model} passed fallback validation ` +
        `(verdict: ${validationResult.validationVerdict}, mode: ${ctx.isPatientSessionActive ? 'emergency' : 'full'})`,
      );
    }

    const callStart = Date.now();
    try {
      console.log(`[Provider Router] ${skillId}: trying ${entry.provider}/${entry.model}${i > 0 ? ' (fallback)' : ''}`);
      const text = await callProvider(entry, mergedConfig);
      recordSuccess(entry.provider);

      if (i > 0) fallbackUsed = true;
      attempts.push({ provider: entry.provider, model: entry.model });

      // Track successful fallback call performance
      if (i > 0) {
        trackFallbackPerformance({
          skillId,
          provider: entry.provider,
          model: entry.model,
          latencyMs: Date.now() - callStart,
          success: true,
          timestamp: new Date().toISOString(),
        });
      }

      console.log(
        `[Provider Router] ${skillId}: success via ${entry.provider}/${entry.model}` +
        (fallbackUsed ? ' (fallback was used)' : ''),
      );

      return {
        text,
        provider: entry.provider,
        model: entry.model,
        fallbackUsed,
        piiRedacted: false, // Caller upgrades to true after rehydration
        attempts,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error(`[Provider Router] ${skillId}: ${entry.provider}/${entry.model} failed — ${errorMsg}`);
      recordFailure(entry.provider);
      attempts.push({ provider: entry.provider, model: entry.model, error: errorMsg });

      // Track failed fallback call performance (i > 0 = fallback attempt)
      if (i > 0) {
        trackFallbackPerformance({
          skillId,
          provider: entry.provider,
          model: entry.model,
          latencyMs: Date.now() - callStart,
          success: false,
          error: errorMsg,
          timestamp: new Date().toISOString(),
        });
      }

      fallbackUsed = true;
    }
  }

  // All providers exhausted — throw with full attempt history
  const attemptSummary = attempts
    .map((a) => `${a.provider}/${a.model}: ${a.error || 'unknown'}`)
    .join('; ');
  throw new Error(
    `[Provider Router] ${skillId}: all providers failed. Attempts: ${attemptSummary}`,
  );
}

// --- Generate (single-turn text) ---

function callGenerate(
  entry: ChainEntry,
  config: SkillConfig,
  systemPrompt: string,
  userMessage: string,
): Promise<string> {
  switch (entry.provider) {
    case 'claude':
      return claudeGenerate(entry.model, systemPrompt, userMessage, config.temperature, config.maxTokens);
    case 'gemini':
      return geminiGenerate(entry.model, systemPrompt, userMessage, config.temperature, config.maxTokens);
    default:
      throw new Error(`Unsupported provider: ${entry.provider}`);
  }
}

export async function routedGenerate(
  skillId: SkillId,
  systemPrompt: string,
  userMessage: string,
  overrides?: Partial<SkillConfig>,
  fallbackContext?: FallbackContext,
): Promise<RouterResponse> {
  // PII redaction — per-call scope, never shared across concurrent calls
  const sysRedaction = redact(systemPrompt);
  const msgRedaction = redact(userMessage);
  const combinedMap = mergeRedactionMaps(sysRedaction.map, msgRedaction.map);

  const response = await routeWithFallback(skillId, overrides, (entry, config) =>
    callGenerate(entry, config, sysRedaction.redacted, msgRedaction.redacted),
    fallbackContext,
  );

  // Rehydrate PII back into the AI response
  response.text = rehydrate(response.text, combinedMap);
  response.piiRedacted = combinedMap.size > 0;
  return response;
}

// --- Chat (multi-turn) ---

function callChat(
  entry: ChainEntry,
  config: SkillConfig,
  systemPrompt: string,
  history: RouterMessage[],
  userMessage: string,
  metadata?: GeminiCallMetadata,
): Promise<string> {
  switch (entry.provider) {
    case 'claude':
      return claudeChat(entry.model, systemPrompt, toClaudeHistory(history), userMessage, config.temperature, config.maxTokens, metadata);
    case 'gemini':
      return geminiChat(entry.model, systemPrompt, toGeminiHistory(history), userMessage, config.temperature, config.maxTokens, metadata);
    default:
      throw new Error(`Unsupported provider: ${entry.provider}`);
  }
}

export async function routedChat(
  skillId: SkillId,
  systemPrompt: string,
  history: RouterMessage[],
  userMessage: string,
  overrides?: Partial<SkillConfig>,
  metadata?: GeminiCallMetadata,
  fallbackContext?: FallbackContext,
): Promise<RouterResponse> {
  // PII redaction — per-call scope
  const sysRedaction = redact(systemPrompt);
  const msgRedaction = redact(userMessage);
  const { redactedMessages, map: historyMap } = redactMessages(history);
  const combinedMap = mergeRedactionMaps(sysRedaction.map, msgRedaction.map, historyMap);

  const response = await routeWithFallback(skillId, overrides, (entry, config) =>
    callChat(entry, config, sysRedaction.redacted, redactedMessages, msgRedaction.redacted, metadata),
    fallbackContext,
  );

  // Rehydrate PII back into the AI response
  response.text = rehydrate(response.text, combinedMap);
  response.piiRedacted = combinedMap.size > 0;
  return response;
}

// --- Vision (image + text) ---

function callVision(
  entry: ChainEntry,
  config: SkillConfig,
  systemPrompt: string,
  imageBase64: string,
  mimeType: string,
  userMessage: string,
): Promise<string> {
  switch (entry.provider) {
    case 'claude':
      return claudeVision(entry.model, systemPrompt, imageBase64, mimeType, userMessage, config.temperature, config.maxTokens);
    case 'gemini':
      return geminiVision(entry.model, systemPrompt, imageBase64, mimeType, userMessage, config.temperature, config.maxTokens);
    default:
      throw new Error(`Unsupported provider: ${entry.provider}`);
  }
}

export async function routedVision(
  skillId: SkillId,
  systemPrompt: string,
  imageBase64: string,
  mimeType: string,
  userMessage: string,
  overrides?: Partial<SkillConfig>,
  fallbackContext?: FallbackContext,
): Promise<RouterResponse> {
  // PII redaction — text only; image cannot be redacted at this layer
  const sysRedaction = redact(systemPrompt);
  const msgRedaction = redact(userMessage);
  const combinedMap = mergeRedactionMaps(sysRedaction.map, msgRedaction.map);

  const response = await routeWithFallback(skillId, overrides, (entry, config) =>
    callVision(entry, config, sysRedaction.redacted, imageBase64, mimeType, msgRedaction.redacted),
    fallbackContext,
  );

  // Rehydrate PII back into the AI response
  response.text = rehydrate(response.text, combinedMap);
  response.piiRedacted = combinedMap.size > 0;
  return response;
}
